import type { RootGenerator } from './root.generator';
import type { RootStore } from './root.store';

/**
 * Keeps the demo generators out of the customer bundle.
 *
 * `root.store.ts` used to `import { RootGenerator }` statically and construct it unconditionally,
 * so every paying provider customer downloaded and parsed the entire demo fixture set — ~82KB
 * minified, ~24KB gzipped across fourteen generator modules plus `@faker-js/faker` — to run code
 * that exists for a sales demo. A static import cannot be tree-shaken out when the value it binds
 * is referenced in a live constructor.
 *
 * ## Why a proxy rather than changing the stores
 *
 * `import()` is asynchronous and `RootStore`'s constructor is not, so the sixteen stores cannot be
 * handed a real generator when they are built. The alternative — giving every store a
 * `() => Generator` and touching all 105 call sites inside them — is the "careful work with no
 * behavioural cover" FE-61 warns about, on code that has no tests. A proxy leaves every store
 * signature and every call site exactly as it is.
 *
 * The proxy forwards **synchronously**. It deliberately does not wrap calls in promises: some
 * generator methods are synchronous and their results are used directly (`payers.store.ts` reads
 * `loadPayersListing(...)` without awaiting), so a promise-returning proxy would break them in a
 * way types would not catch.
 *
 * ## What guarantees the generators are there before anything asks
 *
 * `PrivateRoute.componentDidMount` awaits `rootStore.checkSession()` and renders a progress bar
 * until it resolves — nothing inside the authenticated shell exists before that. `checkSession`
 * awaits `load()` when demo mode is on, so by the time a page can call a store, the generators are
 * constructed.
 *
 * If that ordering is ever broken the proxy throws by name rather than returning undefined, which
 * is the NS-14 direction: a demo screen that fails visibly is safe, one that quietly renders
 * something else is not.
 */
export class DemoGeneratorHost {
    private root?: RootGenerator;
    private loading?: Promise<void>;

    /**
     * Load and construct the generators. Idempotent and safe to call concurrently — the second
     * caller awaits the first one's import rather than building a second dataset.
     */
    public load = async (rootStore: RootStore): Promise<void> => {
        if (this.root) {
            return;
        }
        if (!this.loading) {
            this.loading = import('./root.generator').then(module => {
                this.root = new module.RootGenerator(rootStore);
            });
        }
        return this.loading;
    };

    /** True once the generators exist. */
    public get isLoaded(): boolean {
        return this.root !== undefined;
    }

    /**
     * A stand-in for one generator, resolved on first use.
     *
     * Typed as the generator itself so the stores are unchanged. The cast is the point of the
     * class: callers get the real interface, and the indirection is invisible to them.
     */
    public proxy = <K extends keyof RootGenerator>(key: K): RootGenerator[K] => {
        return new Proxy(
            {},
            {
                // An arrow handler, so `this` is the host lexically. A `const host = this` alias
                // trips `@typescript-eslint/no-this-alias`, which `tsc --noEmit` and the unit
                // suite both pass over — it only surfaces in `craco build`.
                get: (_target, property): unknown => {
                    if (!this.root) {
                        throw new Error(
                            `Demo generators were used before they finished loading (${String(key)}.${String(property)}). ` +
                                'checkSession() awaits DemoGeneratorHost.load() before the authenticated shell renders, ' +
                                'so this means something reached a store outside that path.',
                        );
                    }
                    const generator = this.root[key] as unknown as Record<string | symbol, unknown>;
                    const value = generator[property];
                    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(generator) : value;
                },
            },
        ) as RootGenerator[K];
    };
}
